import { computed, onBeforeUnmount, onMounted, readonly, ref } from 'vue';

import { httpService } from '../services/httpService';
import { eventBus } from '../eventBus';
import { log, error as logError } from '../utils/DebugLogger';

import '../contracts/Chamados_NaoLidosResponse';
import '../contracts/Chamados_NovidadesResponse';
import type { Chamados_NovidadeRow } from '../contracts/Chamados_NovidadeRow';

/**
 * O contador de chamados com novidade — a fonte do badge.
 *
 * ---------------------------------------------------------------------------
 * POR QUE POLLING, E NÃO SSE
 * ---------------------------------------------------------------------------
 * Decisão medida, não preferência. Um stream SSE saindo do app-api seguraria
 * um worker de PHP-FPM pela vida inteira da conexão, e `pm.max_children = 50`
 * é o teto de concorrência da API INTEIRA — 50 abas abertas parariam o
 * sistema, e muito antes disso já não sobraria worker para requisição alguma.
 * É o mecanismo da task #116, só que por desenho.
 *
 * SSE por um serviço Node seria viável, mas o Node não sabe quando um chamado
 * muda: ele teria de consultar o MySQL, ou seja, o polling continuaria
 * existindo — só que com um serviço a mais no caminho. E notificação de
 * chamado tolera 60 segundos.
 *
 * O endpoint que este composable consome é o MESMO que um SSE precisaria para
 * montar o evento. Trocar depois é mexer aqui, não no domínio.
 *
 * ---------------------------------------------------------------------------
 * ESTADO ÚNICO, INTENCIONALMENTE FORA DA FUNÇÃO
 * ---------------------------------------------------------------------------
 * Os refs abaixo são de MÓDULO, ao contrário do `useChamados()`, cujos refs
 * são por instância. A razão é o custo: se cada componente que mostra o badge
 * tivesse o próprio estado, cada um abriria o próprio timer, e o número de
 * requisições passaria a depender de quantos lugares desenham o sino.
 *
 * Com estado único há UM timer para a aba inteira, não importa quantos
 * componentes leiam o contador.
 */

const URL_NAO_LIDOS = '/index.php/chamados/nao-lidos';
const URL_NOVIDADES = '/index.php/chamados/novidades';

/**
 * 60 s. Frequente o bastante para a pessoa não sentir atraso, raro o bastante
 * para ser irrelevante: com 50 usuários logados isso é menos de uma consulta
 * por segundo no total — menos que abrir uma tela de OS.
 */
export const INTERVALO_MS = 60_000;

const total = ref(0);
const carregando = ref(false);

/** Quantos componentes estão usando o contador agora. O timer segue isto. */
let assinantes = 0;
let timer: ReturnType<typeof setInterval> | null = null;

/**
 * Consulta o contador.
 *
 * Falha em SILÊNCIO de propósito: este código roda de minuto em minuto, em
 * segundo plano, sem ninguém ter pedido. Uma queda de rede momentânea não
 * pode encher a tela de mensagens de erro — o número simplesmente fica com o
 * último valor conhecido até a próxima tentativa.
 */
async function buscar(): Promise<void> {
  if (carregando.value) {
    return;
  }

  carregando.value = true;

  try {
    const resposta = await httpService.get(URL_NAO_LIDOS, {
      responseContract: 'Chamados_NaoLidosResponse',
    });

    if (resposta.success && resposta.dados) {
      total.value = Number(resposta.dados.total ?? 0);
    }
  } catch (e) {
    logError('useChamadosNaoLidos', 'buscar', 'Falha ao ler o contador (silenciosa)', e);
  } finally {
    carregando.value = false;
  }
}

/**
 * Só consulta com a ABA VISÍVEL.
 *
 * É a diferença entre polling e desperdício. Um usuário com o sistema aberto
 * numa aba de fundo a semana inteira geraria 10 mil requisições por dia sem
 * ninguém olhar o resultado — e é o caso mais comum em franquia, onde o
 * sistema fica aberto o expediente todo.
 *
 * Ao voltar para a aba, consulta na hora: o que a pessoa quer ver é o estado
 * de AGORA, não esperar o próximo tique.
 */
function aoMudarVisibilidade(): void {
  if (document.visibilityState === 'visible') {
    void buscar();
  }
}

function comecar(): void {
  if (timer !== null) {
    return;
  }

  timer = setInterval(() => {
    if (document.visibilityState === 'visible') {
      void buscar();
    }
  }, INTERVALO_MS);

  document.addEventListener('visibilitychange', aoMudarVisibilidade);

  // O evento que o próprio domínio já emite depois de qualquer ação. Sem
  // isto, responder um chamado deixaria o badge desatualizado por até um
  // minuto — e a pessoa veria o número contradizer o que ela acabou de fazer.
  eventBus.on('chamados.refresh', buscar);
}

function parar(): void {
  if (timer !== null) {
    clearInterval(timer);
    timer = null;
  }

  document.removeEventListener('visibilitychange', aoMudarVisibilidade);
  eventBus.off('chamados.refresh', buscar);
}

/**
 * As linhas do painel. Buscadas SÓ no clique — nunca no polling.
 *
 * É a metade da decisão que mantém o recurso barato: o tique de 60s pede um
 * número; a lista sai uma vez, quando alguém abre o painel. Chamar isto no
 * timer faria o filtro e a serialização rodarem por minuto por usuário para
 * um painel que quase ninguém abre.
 */
export async function buscarNovidades(): Promise<{ rows: Chamados_NovidadeRow[]; total: number }> {
  const resposta = await httpService.get(URL_NOVIDADES, {
    responseContract: 'Chamados_NovidadesResponse',
  });

  if (!resposta.success || !resposta.dados) {
    throw new Error(resposta.error || 'Erro ao carregar as novidades');
  }

  // O total do painel manda no badge: se as duas consultas discordarem (a
  // pessoa leu algo em outra aba entre uma e outra), quem está na tela agora
  // é o painel.
  total.value = Number(resposta.dados.total ?? 0);

  return {
    rows: (resposta.dados.rows ?? []) as Chamados_NovidadeRow[],
    total: total.value,
  };
}

export function useChamadosNaoLidos() {
  onMounted(() => {
    assinantes += 1;
    comecar();
    void buscar();
  });

  onBeforeUnmount(() => {
    assinantes -= 1;

    // Só desliga quando o ÚLTIMO componente sai. Desligar no primeiro
    // desmonte pararia o contador enquanto outro lugar ainda o desenha —
    // o badge congelaria sem nada indicar por quê.
    if (assinantes <= 0) {
      assinantes = 0;
      parar();
    }
  });

  return {
    total: readonly(total),
    carregando: readonly(carregando),
    /** `0` some da tela: um badge zerado é ruído permanente. */
    temNovidade: computed(() => total.value > 0),
    atualizar: buscar,
    buscarNovidades,
  };
}

/** Só para os testes: zera o estado de módulo entre um caso e outro. */
export function __resetNaoLidos(): void {
  parar();
  assinantes = 0;
  total.value = 0;
  carregando.value = false;
  log('useChamadosNaoLidos', '__resetNaoLidos', 'Estado de modulo zerado');
}
